'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchOrgs } from '@/lib/api/orgs';
import type { Workspace } from '@/types/shared/org';

const STORAGE_KEY = 'zenopus_active_workspace_id';

type WorkspaceContextType = {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  isLoading: boolean;
  setActiveWorkspace: (org: Workspace) => void;
  refreshWorkspaces: () => Promise<void>;
};

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

function isSwitchableWorkspace(org: Workspace): boolean {
  // Project-only memberships (the user has a seat on a shared project but is
  // NOT an org member) must never be used as the active workspace: their
  // billing belongs to a different org, and placing new projects there was
  // the root cause of the multi-tenant credit leak where a project-member
  // saw their projects disappear into the owning org's (empty) credit
  // bucket. Org memberships and the user's own personal workspace are the
  // only legitimate active-workspace candidates.
  return org.membershipKind !== 'project';
}

function resolveActive(workspaces: Workspace[], savedId: string | null): Workspace | null {
  if (workspaces.length === 0) return null;
  const switchable = workspaces.filter(isSwitchableWorkspace);
  const pool = switchable.length > 0 ? switchable : workspaces;
  if (savedId) {
    const found = pool.find((w) => w.id === savedId);
    if (found) return found;
  }
  return pool.find((w) => w.isPersonal) ?? pool[0];
}

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------
// All workspace-context state mutations flow through a reducer so each
// transition is a single, named action — that's the canonical shape for
// the React Compiler `set-state-in-effect` rule. The previous design used
// three independent useState pairs and a load effect that synchronously
// fired three setters when the user logged out; the compiler correctly
// flagged that as a derived-state cascade. Folding the three pieces into
// one reducer turns "auth state changed" into a single `dispatch` of
// `RESET_FOR_LOGOUT`, which the rule treats as a real transition rather
// than three derivations.

type WorkspaceState = {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  isLoading: boolean;
};

type WorkspaceAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; workspaces: Workspace[]; savedId: string | null }
  | { type: 'LOAD_ERROR' }
  | { type: 'RESET_FOR_LOGOUT' }
  | { type: 'SET_ACTIVE'; workspace: Workspace }
  | { type: 'REFRESH_SUCCESS'; workspaces: Workspace[] };

const initialWorkspaceState: WorkspaceState = {
  workspaces: [],
  activeWorkspace: null,
  isLoading: true,
};

function workspaceReducer(state: WorkspaceState, action: WorkspaceAction): WorkspaceState {
  switch (action.type) {
    case 'LOAD_START':
      // Already-loading state: avoid extra renders.
      if (state.isLoading) return state;
      return { ...state, isLoading: true };
    case 'LOAD_SUCCESS':
      return {
        workspaces: action.workspaces,
        activeWorkspace: resolveActive(action.workspaces, action.savedId),
        isLoading: false,
      };
    case 'LOAD_ERROR':
      return { workspaces: [], activeWorkspace: null, isLoading: false };
    case 'RESET_FOR_LOGOUT':
      // Single transition for the logout case — replaces the three
      // synchronous setState calls that triggered the React Compiler's
      // set-state-in-effect rule.
      if (!state.isLoading && state.workspaces.length === 0 && state.activeWorkspace === null) {
        return state;
      }
      return { workspaces: [], activeWorkspace: null, isLoading: false };
    case 'SET_ACTIVE':
      return { ...state, activeWorkspace: action.workspace };
    case 'REFRESH_SUCCESS': {
      const savedId =
        state.activeWorkspace?.id ?? (typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null);
      return {
        ...state,
        workspaces: action.workspaces,
        activeWorkspace: resolveActive(action.workspaces, savedId),
      };
    }
    // Defensive: the switch is exhaustive over the closed WorkspaceAction union,
    // so the default arm is unreachable (kept for forward-compat type-safety).
    /* v8 ignore next 2 */
    default:
      return state;
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();

  const [state, dispatch] = useReducer(workspaceReducer, initialWorkspaceState);
  const { workspaces, activeWorkspace, isLoading } = state;

  // Mount-only load: fires once per provider lifetime when auth resolves.
  // The actual setState lives inside an async chain (LOAD_SUCCESS /
  // LOAD_ERROR dispatched after the await), so the synchronous effect
  // body issues no setState — the rule is satisfied without disabling.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      dispatch({ type: 'RESET_FOR_LOGOUT' });
      return;
    }

    let cancelled = false;
    void (async () => {
      dispatch({ type: 'LOAD_START' });
      try {
        const orgs = await fetchOrgs();
        if (cancelled) return;
        const savedId = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
        dispatch({ type: 'LOAD_SUCCESS', workspaces: orgs, savedId });
      } catch {
        if (cancelled) return;
        // Fehler still abfangen — Workspaces bleiben leer
        dispatch({ type: 'LOAD_ERROR' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user]);

  const setActiveWorkspace = useCallback((org: Workspace) => {
    if (!isSwitchableWorkspace(org)) {
      // Defensive guard: refuse to switch INTO a project-only membership.
      // Any UI surface that still offers it as a target should be treating
      // it as "shared with me" and routing directly to the project.
      return;
    }
    dispatch({ type: 'SET_ACTIVE', workspace: org });
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, org.id);
    }
  }, []);

  const refreshWorkspaces = useCallback(async () => {
    try {
      const orgs = await fetchOrgs();
      dispatch({ type: 'REFRESH_SUCCESS', workspaces: orgs });
    } catch {
      // Fehler still abfangen
    }
  }, []);

  const value = useMemo<WorkspaceContextType>(
    () => ({
      workspaces,
      activeWorkspace,
      isLoading,
      setActiveWorkspace,
      refreshWorkspaces,
    }),
    [workspaces, activeWorkspace, isLoading, setActiveWorkspace, refreshWorkspaces],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextType {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
}
